import { Modal, Select } from "antd";
import { isEmpty, sortBy } from "lodash";
import React, { useEffect, useState } from "react";
import CustomCheckbox from "../commonComponents/checkbox";
import CustomButton from "../commonComponents/button";

// Display name for a user row coming from `getUsers` — mirrors the same helper
// in `commonComponents/userSelector` (fullName, else firstName + lastName).
export const getUserLabel = user => {
  if (!user) return "";
  if (user.fullName) return user.fullName;
  if (user.firstName || user.lastName)
    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return "";
};

// Name shown on the "Lead" row. Every lead query in the app selects `fullName`;
// first/last is the fallback for the few shapes that carry the parts instead.
// Deliberately NO `name` fallback: the information form can be handed an
// OPPORTUNITY as its `lead` prop, and an opportunity's `name` is the
// opportunity title — rendering that as the lead name would be wrong.
export const getLeadLabel = lead => {
  if (!lead) return "";
  return lead.fullName || `${lead.firstName || ""} ${lead.lastName || ""}`.trim();
};

// "2014 Jeep Wrangler (Buy)" for the primary (or first non-deleted) vehicle of
// the opportunity. Returns "" when the opportunity has no vehicles, and the
// caller then hides the row.
export const getVehicleLabel = vehicles => {
  const active = (vehicles || []).filter(v => v && !v.isDeleted);
  if (isEmpty(active)) return "";
  const vehicle = active.find(v => v.isPrimary) || active[0];
  const title = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ");
  const interest = vehicle.customerInterest
    ? `${vehicle.customerInterest.charAt(0)}${vehicle.customerInterest.slice(1).toLowerCase()}`
    : "";
  if (!title) return interest ? `(${interest})` : "";
  return interest ? `${title} (${interest})` : title;
};

/**
 * One-line summary of what a staged reassignment will move, with a way back
 * into the dialog.
 *
 * Without this the choice is invisible and unreachable: the row just shows the
 * new owner, and re-picking that same owner fires no change event, so a rep who
 * wants to amend the transfer options would have to select someone else and
 * then switch back (which also resets the options).
 *
 * Renders only for rows whose owner actually changed — the caller decides that.
 */
export const ReassignTransferSummary = ({ row, onEdit, disabled = false }) => {
  const moving = [
    row?.isTaskAssignment ? "pending tasks" : null,
    row?.isAppointmentAssignment ? "active appointments" : null,
  ].filter(Boolean);

  return (
    // Sits in the gap below the Form.Item's input, so the negative top margin
    // only closes part of that 24px gutter — enough pulled up to read as
    // attached to the field, with breathing room under the select itself.
    <div className="flex flex-row items-center gap-2 -mt-2 mb-3 text-xs font-[400]">
      <span className="text-[#64748B]">
        {moving.length ? `Transferring ${moving.join(" and ")}` : "Transferring nothing"}
      </span>
      {/* htmlType MUST be "button": this renders inside the opportunity <Form>,
          and CustomButton defaults htmlType to "" — an empty/invalid type
          attribute falls back to "submit", so the click would open the dialog
          AND submit the form, firing the save mutation. */}
      <CustomButton
        type="link"
        size="small"
        htmlType="button"
        btnText="Edit"
        disabled={disabled}
        handleClick={onEdit}
        className="p-0 h-auto text-xs"
      />
    </div>
  );
};

/**
 * EAI-1877 — confirmation dialog shown when an opportunity user (owner) is
 * being changed on an EXISTING assignment. The new assignee can still be
 * swapped from inside the dialog, and the two toggles decide whether the
 * OUTGOING owner's open tasks / appointments move across to whoever replaces
 * them.
 *
 * Because the dialog opens per owner change, each changed owner gets its own
 * pair of choices — which is the per-owner granularity the backend's
 * `UserMapping` flags expect (move A's appointments without moving A's tasks,
 * in the same save that moves both of B's).
 *
 * The values come back through `onAssign` named exactly as the mutation input
 * fields (`isTaskAssignment` / `isAppointmentAssignment`) so callers can spread
 * them onto the row; `helpers/opportunityUserPayload` decides what actually
 * ships.
 */
export default function ReassignOwnerModal({
  open,
  userTypeName,
  leadName,
  vehicleLabel,
  users = [],
  value,
  // Both default to ticked, matching the design and the bulk reassign drawer.
  // Nothing moves unless the caller sends the flag, so an untick is a real
  // "leave this owner's work where it is". Reopening the dialog to amend an
  // already-staged reassignment passes that row's current choices instead.
  defaultTransferTasks = true,
  defaultTransferAppointments = true,
  // Reopened from the row summary to amend a staged reassignment rather than
  // to make a new one: the owner already moved, so the wording changes and
  // Cancel just closes (the caller decides that part).
  isEditing = false,
  loading = false,
  onAssign = () => {},
  onCancel = () => {},
}) {
  const [selectedUserId, setSelectedUserId] = useState(value ?? null);
  const [transferTasks, setTransferTasks] = useState(defaultTransferTasks);
  const [transferAppointments, setTransferAppointments] = useState(defaultTransferAppointments);

  // Re-seed every time the dialog is (re)opened for a different row, so a
  // previous run's checkbox state doesn't leak into the next reassignment.
  useEffect(() => {
    if (!open) return;
    setSelectedUserId(value ?? null);
    setTransferTasks(defaultTransferTasks);
    setTransferAppointments(defaultTransferAppointments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  return (
    <Modal
      open={open}
      title={isEditing ? "Edit reassignment" : "Reassign owner"}
      okText={isEditing ? "Update" : "Assign"}
      cancelText="Cancel"
      destroyOnClose
      maskClosable={false}
      confirmLoading={loading}
      okButtonProps={{ disabled: !selectedUserId }}
      onOk={() =>
        // Named as the `UserMapping` input fields so the caller can spread them
        // straight onto the owner row.
        onAssign({
          userId: selectedUserId,
          isTaskAssignment: transferTasks,
          isAppointmentAssignment: transferAppointments,
        })
      }
      onCancel={onCancel}
    >
      {(leadName || vehicleLabel) && (
        <div className="bg-[#F8FAFC] rounded-md p-3 flex flex-col gap-2">
          {leadName && (
            <div className="flex flex-row items-start gap-3">
              <span className="text-xs font-[400] text-[#64748B] w-[130px] shrink-0">Lead</span>
              <span className="text-sm font-[600] text-grey-700">{leadName}</span>
            </div>
          )}
          {vehicleLabel && (
            <div className="flex flex-row items-start gap-3">
              <span className="text-xs font-[400] text-[#64748B] w-[130px] shrink-0">
                Vehicle of interest
              </span>
              <span className="text-sm font-[600] text-grey-700">{vehicleLabel}</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        <div className="text-sm font-[600] text-grey-700">New assignee</div>
        <div className="text-xs font-[400] text-[#64748B] mt-2 mb-1">{userTypeName}</div>
        <Select
          showSearch
          className="w-full"
          value={selectedUserId ?? undefined}
          onChange={val => setSelectedUserId(val)}
          placeholder={`Select ${userTypeName || "user"}`}
          optionFilterProp="label"
          options={sortBy(users, u => getUserLabel(u)).map(u => ({
            value: +u.id,
            label: `${getUserLabel(u)}${
              !isEmpty(u?.companyRole) ? ` (${u.companyRole.name})` : ""
            }`,
          }))}
        />
      </div>

      <div className="mt-4">
        {/* Labels follow the design. The bulk reassign drawer words the same
            two options "Re-assign tasks" / "Re-assign appointments" under a
            "Reassignment options" heading — worth aligning if the team wants
            one wording across both flows. */}
        <div className="text-sm font-[600] text-grey-700 mb-2">Transfer to new assignee</div>
        <div className="flex flex-col gap-2">
          <CustomCheckbox
            checked={transferTasks}
            checkboxLabel="All pending tasks"
            handleChange={e => setTransferTasks(e.target.checked)}
          />
          <CustomCheckbox
            checked={transferAppointments}
            checkboxLabel="All active appointments"
            handleChange={e => setTransferAppointments(e.target.checked)}
          />
        </div>
      </div>
    </Modal>
  );
}
